import React, {useContext, useState, useRef, useCallback, useMemo, useEffect} from 'react';
import {useDispatch, useStore} from 'react-redux';
import {LocationPoint} from '@/common/interfaces/location';
import {useLocalizer} from '@/common/services/localizer';
import {useErrorInformerPush} from '@/common/services/ErrorInformer';
import {debounce} from '@/common/helpers/utils/debounce';
import {useUpdateEffect} from '@/common/helpers/hooks/useUpdateEffect';
import {ServicesContext} from '@/app/contexts/ServicesContext';
import {StoreState, StoreThunkDispatch} from '@/app/store';
import {selectSearchHistory, selectSearchHistoryIsLoaded} from '@/app/store/locationsHistory/selectors';
import {fetchSearchHistoryAction, resetSearchSuggest} from '@/app/store/locationsHistory/actions';
import {selectDeviceGeneralSettlement, selectDeviceGeolocationSettlement} from '@/app/store/geolocation/selectors';
import {determinateGeneralSettlement, determinateExactSettlement} from '@/app/store/geolocation/actions';
import {selectDefaultLocationInfo} from '@/app/store/config/selectors';
import {fetchDefaultLocationInfoAction} from '@/app/store/config/actions';

export type SuggestLocationItem = LocationPoint & {userDefault?: 'determined' | 'defined'};

export function useSearchSuggest({
	suggestCountLimit,
	debounceInterval,
}: {
	suggestCountLimit: number;
	debounceInterval: number;
}) {
	const {backendAPI} = useContext(ServicesContext)!;
	const dispatch = useDispatch<StoreThunkDispatch>();
	const errorInformerPush = useErrorInformerPush();

	const {language} = useLocalizer();
	const store = useStore<StoreState>();
	const [searchString, setSearchString] = useState('');
	const [suggestList, setSuggestList] = useState<SuggestLocationItem[]>([]);
	const [suggestShowAllowed, setSuggestShowAllowed] = useState(false);

	// Make ref to an actual value of searchString to use in async handlers
	const actualSearchString = useRef(searchString);
	actualSearchString.current = searchString;

	// Set the search location history list as a suggesting
	const setSearchHistoryLocationAsSuggest = useCallback(async () => {
		let storeState = store.getState();
		const searchHistoryIsLoaded = selectSearchHistoryIsLoaded(storeState);
		const defaultLocationInfo = selectDefaultLocationInfo(storeState);
		const deviceGeneralSettlement = selectDeviceGeneralSettlement(storeState);

		// Fetch an actual search history locations list if it is ot loaded before
		// and determine default geolocation when it is not loaded before and the user has not a defined default location
		if (!searchHistoryIsLoaded || !deviceGeneralSettlement) {
			const limit = suggestCountLimit;
			await Promise.all(
				[
					!searchHistoryIsLoaded &&
						dispatch(fetchSearchHistoryAction({language, limit})).catch(error => console.error(error)),
					!deviceGeneralSettlement &&
						!defaultLocationInfo &&
						dispatch(determinateGeneralSettlement({language})).catch(error => console.error(error)),
				].filter(Boolean) as Promise<unknown>[],
			);
		}

		// Avoid the suggest list rewrite when during search history list fetching was loaded suggest data for a search string
		if (actualSearchString.current) {
			return;
		}

		const newList: SuggestLocationItem[] = selectSearchHistory(store.getState()).slice();

		// Add user's default location to the list
		storeState = store.getState();
		const userDefaultDefinedLocation = selectDefaultLocationInfo(storeState);
		const userDefaultLocation =
			userDefaultDefinedLocation ||
			selectDeviceGeolocationSettlement(storeState) ||
			selectDeviceGeneralSettlement(storeState);

		if (userDefaultLocation) {
			newList.unshift({
				...userDefaultLocation,
				userDefault: userDefaultDefinedLocation ? 'defined' : 'determined',
			});
		}

		setSuggestList(newList);
	}, [language]);

	// Handle focus in to the search field
	const handleFieldFocusin = useCallback(() => {
		// Allow the suggest list show
		setSuggestShowAllowed(true);

		// On focus to the empty field set the search history list as a suggest
		if (!searchString) {
			setSearchHistoryLocationAsSuggest();
		}
	}, [!searchString, setSearchHistoryLocationAsSuggest]);

	// Handle focus out from the search field
	const handleFieldFocusout = useCallback(() => {
		// Hide the suggest list
		setSuggestShowAllowed(false);
	}, []);

	// A suggest list search handler with debounce
	const handleSearch = useMemo(
		() =>
			debounce(async (value: string) => {
				let newList;
				try {
					newList = await backendAPI.search.getSearchSuggests(value, language, suggestCountLimit);
				} catch (error) {
					errorInformerPush(error);
					throw error;
				}

				// Ignore a new suggest list if the value of the search string is changed (requests concurrency case)
				if (value !== actualSearchString.current) {
					return;
				}

				setSuggestList(newList);
			}, debounceInterval),
		[language, suggestCountLimit],
	);

	// Fetch search suggest on the search string change
	const handleSearchStringChange = useCallback(
		async (event: React.ChangeEvent<HTMLInputElement>) => {
			const value = event.currentTarget.value;
			setSearchString(value);

			if (!value) {
				setSearchHistoryLocationAsSuggest();
			} else {
				handleSearch(value);
			}
		},
		[setSearchHistoryLocationAsSuggest, handleSearch],
	);

	const handleSearchStringClean = useCallback(() => {
		setSearchString('');
		setSuggestList([]);
	}, []);

	// Break waiting search
	useEffect(() => {
		return () => handleSearch.cancel();
	}, [handleSearch]);

	// Actualize the search suggest list and default location, reset history list on language change
	useUpdateEffect(() => {
		dispatch(resetSearchSuggest());

		if (searchString && suggestList.length) {
			handleSearch(searchString);
		}

		const state = store.getState();
		if (selectDefaultLocationInfo(state)) {
			dispatch(fetchDefaultLocationInfoAction({language}));
			return;
		}
		if (selectDeviceGeolocationSettlement(state)) {
			dispatch(determinateExactSettlement({language}));
		}
		if (selectDeviceGeneralSettlement(state)) {
			dispatch(determinateGeneralSettlement({language}));
		}
	}, [language]);

	return {
		searchString,
		suggestList,
		suggestShowAllowed,
		handleFieldFocusin,
		handleFieldFocusout,
		handleSearchStringChange,
		handleSearchStringClean,
	};
}

useSearchSuggest.displayName = 'useSearchSuggest';
